import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { aliasedModelSlug } from "@/lib/slug-aliases";
import {
  getVehiclesForModel,
  getRelatedLinks,
  getSpecsForModel,
  getNhtsaExtrasForModel,
  type VehicleRow,
} from "@/lib/vehicle-queries";
import { ALL_DATA_TYPES, SPEC_DATA_TYPES, getDataTypeInfo, type DataTypeKey } from "@/lib/vehicle-data";
import { RelatedLinks } from "@/components/RelatedLinks";
import { breadcrumbLd, vehicleLd, itemListLd, faqLd, SITE_OG_IMAGE, pageTitle } from "@/lib/seo";
import { buildModelFaqs } from "@/lib/model-faqs";
import { FaqBlock } from "@/components/FaqBlock";
import { buildGenerations } from "@/lib/generations";
import { buildMaintenanceYearRows, sitemapWorthyYears } from "@/lib/maintenance-years";

import { isIndexableModel, NOINDEX_META } from "@/lib/index-policy";
import { getIsProvenPage } from "@/lib/index-overrides.functions";
import { MaintenanceSpecLink } from "@/components/MaintenanceSpecLink";
import { maintenanceSpecModelUrl } from "@/lib/maintenance-spec";
import { UsefulTools } from "@/components/UsefulTools";
import { BlufSummary } from "@/components/BlufSummary";
import { buildBlufSummary } from "@/lib/bluf";
import { capRows, compactRows, pickFields, shortIds, stripProvenance } from "@/lib/payload";
import { buildSpecGroups, buildSpecHubNarrative } from "@/lib/spec-hub";

import { getModelComparisons } from "@/lib/compare.functions";
import { isSpecsHubSample } from "@/lib/model-hub-sample";
import { buildModelSpecsSnapshot } from "@/lib/model-specs-snapshot";
import { ModelSpecsSnapshotBlock } from "@/components/ModelSpecsSnapshot";
import { AdSlot } from "@/components/AdSlot";



export const Route = createFileRoute("/$make/$model/")({
  loader: async ({ params }) => {
    const alias = aliasedModelSlug(params.model);
    if (alias) {
      throw redirect({ to: "/$make/$model", params: { make: params.make, model: alias }, statusCode: 301 });
    }
    const [result, related, specResult, provenByData] = await Promise.all([
      getVehiclesForModel({ data: { makeSlug: params.make, modelSlug: params.model } }),
      getRelatedLinks({ data: { makeSlug: params.make, modelSlug: params.model } }),
      getSpecsForModel({
        data: { makeSlug: params.make, modelSlug: params.model, specType: "towing-capacity" },
      }),
      getIsProvenPage({ data: { path: `/${params.make}/${params.model}` } }),
    ]);
    const comparisons = await getModelComparisons({
      data: { makeSlug: params.make, modelSlug: params.model },
    });
    // Never publish an empty model hub. If no EPA records exist for this make/model,
    // treat it as not found so AdSense does not crawl a thin page.
    if (!result.model) {
      throw notFound();
    }
    const extras = await getNhtsaExtrasForModel({ data: { modelId: result.model.id } });
    // Heavy-duty trucks (F-250/F-350 and friends) are absent from EPA data but do
    // have towing/bed/dimension/safety records and live leaf pages. 404-ing the hub
    // broke every internal link pointing at it, so only 404 when there is no data
    // of any kind behind the model.
    const hasAnyData =
      result.vehicles.length > 0 ||
      specResult.availableSpecTypes.length > 0 ||
      extras.dimensions.length > 0 ||
      extras.safety.length > 0;
    if (!hasAnyData) {
      throw notFound();
    }
    // The rebuilt "YEAR Make Model Specs" hub ships to a sample of two models
    // first; only those pages pay the extra payload for the snapshot block.
    const isSample = isSpecsHubSample(params.make, params.model);
    const latestSafety = extras.safety.find((s) => s.overall_rating) ?? null;
    // Hydration payload discipline: the hub renders a fixed set of EPA columns,
    // so every other column (fuel cost, emissions, scores, UUIDs, per-row
    // provenance) is dead weight that crushed the text-to-HTML ratio.
    const HUB_EPA_FIELDS = [
      "id",
      "year",
      "trim",
      "cylinders",
      "displacement",
      "engine_desc",
      "transmission",
      "drive",
      "fuel_type",
      "fuel_type_detail",
      "vehicle_class",
      "atv_type",
      "phev_blended",
      "mpg_city",
      "mpg_hwy",
      "mpg_combined",
      "ev_range_miles",
    ];
    const slim = <T,>(rows: T[], prefix: string) =>
      shortIds(
        stripProvenance(compactRows(rows as unknown as Record<string, unknown>[])),
        prefix,
      ) as unknown as T[];
    const slimVehicles = shortIds(
      stripProvenance(
        compactRows(
          (result.vehicles as unknown as Record<string, unknown>[]).map((v) =>
            pickFields(v, HUB_EPA_FIELDS),
          ),
        ),
      ),
      "v",
    );
    // The overview table renders every shipped row, so cap the oldest EPA
    // configurations — the leaf /mpg page carries the full history.
    const cappedVehicles = capRows(slimVehicles, 80);
    // The year chips must be derived from the FULL history: the 80-row cap keeps
    // only the newest model years, which silently dropped the links to every
    // older ?year= maintenance URL and left them with a single inbound link.
    const maintenanceYears = sitemapWorthyYears(
      buildMaintenanceYearRows(result.vehicles as unknown as any[]),
    );
    return {
      ...result,
      // Identical EPA trim rows and null columns never reach the hydration
      // payload — see src/lib/payload.ts.
      vehicles: cappedVehicles as unknown as typeof result.vehicles,
      maintenanceYears,
      totalVariants: result.vehicles.length,
      variantsTruncated: slimVehicles.length > cappedVehicles.length,

      related,
      availableSpecTypes: specResult.availableSpecTypes,
      towingSpecs: capRows(slim(specResult.specs, "t") as unknown as Record<string, unknown>[], 40) as unknown as typeof specResult.specs,
      // EPA-less models (heavy-duty trucks) would otherwise render an empty
      // overview table, so their manufacturer spec rows are rendered instead.
      fallbackSpecs: (result.vehicles.length === 0
        ? slim(specResult.allSpecs, "f")
        : ([] as typeof specResult.allSpecs)),
      comparisons: comparisons.pairs,
      hasDimensions: extras.dimensions.length > 0,
      hasCurbWeight: extras.dimensions.some((d) => d.curb_weight_lbs != null),
      hasSafety: extras.safety.length > 0,
      provenByData,
      isSample,
      snapshotSpecs: isSample
        ? (capRows(slim(specResult.allSpecs, "s") as unknown as Record<string, unknown>[], 24) as unknown as typeof specResult.allSpecs)
        : [],
      snapshotDimensions: isSample
        ? (capRows(
            slim(
              (extras.dimensions as unknown as Record<string, unknown>[]).map((d) =>
                pickFields(d, [
                  "id",
                  "year",
                  "variant_label",
                  "length_in",
                  "width_in",
                  "height_in",
                  "wheelbase_in",
                  "curb_weight_lbs",
                ]),
              ),
              "d",
            ) as unknown as Record<string, unknown>[],
            12,
          ) as unknown as typeof extras.dimensions)
        : [],
      snapshotSafety:
        isSample && latestSafety?.overall_rating
          ? { year: latestSafety.year, rating: latestSafety.overall_rating }
          : null,
    };



  },
  component: ModelPage,
  head: ({ loaderData, params }) => {
    if (!loaderData?.make || !loaderData?.model) {
      return {
        meta: [
          { title: "Model not found — AutoSpecData" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { make, model } = loaderData;
    const fullName = `${make.name} ${model.name}`;
    const path = `https://autospecdata.com/${params.make}/${params.model}`;
    const vehicles = (loaderData.vehicles ?? []) as VehicleRow[];
    const years = vehicles.map((v) => v.year);
    const bestMpg = vehicles.reduce<number | null>(
      (b, v) => (v.mpg_combined && (!b || v.mpg_combined > b) ? v.mpg_combined : b),
      null,
    );
    const indexable = loaderData.provenByData === true || isIndexableModel({
      makeSlug: params.make,
      modelSlug: params.model,
      latestYear: years.length ? Math.max(...years) : null,
      rowCount: vehicles.length,
    });
    // EPA never rates vehicles over 8,500 lb GVWR, so an MPG-led title on a
    // heavy-duty truck promises data the page cannot show.
    const noEpa = vehicles.length === 0;
    // Sample hubs target current-year "<model> specs" intent, so the year
    // leads both title and description.
    const sampleYear = loaderData.isSample
      ? Math.max(
          ...[
            ...years,
            ...(loaderData.snapshotSpecs ?? []).map((s) => s.year),
            ...(loaderData.snapshotDimensions ?? []).map((d) => d.year),
            0,
          ],
        ) || null
      : null;
    const description = sampleYear
      ? `${sampleYear} ${fullName} specs: MPG, engine, curb weight, dimensions, towing capacity, and fuel tank size — plus every earlier model year. EPA, NHTSA, and manufacturer data.`
      : noEpa
      ? `${fullName} towing capacity, payload, bed dimensions, and fuel tank size by model year, from manufacturer and NHTSA published specifications.`
      : `${fullName} MPG and gas mileage by year, plus engine size, dimensions, fuel cost, and emissions for every model year. Verified EPA data from fueleconomy.gov.`;
    const faqs = buildModelFaqs({
      makeName: make.name,
      modelName: model.name,
      vehicles,
      towingSpecs: loaderData.towingSpecs ?? [],
    });
    return {
      meta: [
        {
          title: pageTitle(
            sampleYear
              ? `${sampleYear} ${fullName} Specs, MPG & Weight`
              : noEpa
                ? `${fullName} Towing Capacity, Payload & Bed Dimensions`
                : `${fullName} MPG, Gas Mileage & Engine Size`,
          ),
        },
        ...(indexable ? [] : NOINDEX_META),
        {
          name: "description",
          content: description,
        },

        { property: "og:title", content: `${fullName} Specs${noEpa ? " & Towing" : " & MPG"} | AutoSpecData` },

        {
          property: "og:description",
          content: noEpa
            ? `${fullName} specifications — towing capacity, payload, bed dimensions, and fuel tank size for every model year.`
            : `${fullName} specifications — MPG, engine specs, fuel cost, and emissions for every model year.`,
        },

        { property: "og:url", content: path },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: make.name, path: `/${params.make}` },
              { name: model.name, path },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            vehicleLd({
              make: make.name,
              model: model.name,
              path,
              yearMin: years.length ? Math.min(...years) : null,
              yearMax: years.length ? Math.max(...years) : null,
              bestMpg,
              fuelType: vehicles[0]?.fuel_type ?? null,
              vehicleClass: vehicles[0]?.vehicle_class ?? null,
              transmission: vehicles[0]?.transmission ?? null,
              drive: vehicles[0]?.drive ?? null,
              description,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            itemListLd(
              `${fullName} specification pages`,
              ALL_DATA_TYPES.filter((dt) => !noEpa || dt === "maintenance-schedule").map((dt) => {
                const info = getDataTypeInfo(dt);
                return { name: `${fullName} ${info.title}`, path: `${path}/${info.slug}` };
              }),
            ),
          ),
        },
        ...(faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(faqLd(faqs)),
              },
            ]
          : []),
      ],
    };
  },
});

function ModelPage() {
  const {
    make,
    model,
    vehicles,
    related,
    availableSpecTypes,
    hasDimensions,
    hasCurbWeight,
    hasSafety,
    towingSpecs,
    fallbackSpecs,
    comparisons,
    isSample,
    snapshotSpecs,
    snapshotDimensions,
    snapshotSafety,
    totalVariants,
    variantsTruncated,
    maintenanceYears: loadedMaintenanceYears,
  } = Route.useLoaderData();


  const params = Route.useParams();


  if (!make || !model) {
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
        <h1 className="text-2xl font-bold text-foreground">Model not found</h1>
        <Link to={`/$make`} params={{ make: params.make }} className="mt-4 inline-block text-primary hover:underline">
          Back to {params.make}
        </Link>
      </div>
    );
  }

  const fullName = `${make.name} ${model.name}`;

  // Group vehicles by year for overview
  const yearMap = new Map<number, typeof vehicles>();
  for (const v of vehicles) {
    if (!yearMap.has(v.year)) yearMap.set(v.year, []);
    yearMap.get(v.year)!.push(v);
  }
  const years = Array.from(yearMap.keys()).sort((a, b) => b - a);

  // The ?year= maintenance URLs were only linked from the model's own
  // maintenance page, so crawlers saw a single inbound link each. Linking the
  // sitemap-worthy years from the model hub gives every one a second source.
  const maintenanceYears: number[] =
    loadedMaintenanceYears ??
    sitemapWorthyYears(buildMaintenanceYearRows(vehicles as unknown as any[]));


  // Best MPG across all vehicles
  const bestMpg = vehicles.reduce(
    (best: number | null, v: VehicleRow) =>
      v.mpg_combined && (!best || v.mpg_combined > best) ? v.mpg_combined : best,
    null as number | null,
  );

  // Year range
  const yearMin = years.length > 0 ? years[years.length - 1] : null;
  const yearMax = years.length > 0 ? years[0] : null;

  const generations = buildGenerations(vehicles);
  const noEpa = vehicles.length === 0;
  const specGroups = noEpa ? buildSpecGroups(fallbackSpecs) : [];
  const specNarrative = noEpa
    ? buildSpecHubNarrative(make.name, model.name, fallbackSpecs)
    : [];
  const hasEv = vehicles.some((v: VehicleRow) => v.ev_range_miles != null);
  const extraTypes: DataTypeKey[] = [
    ...(hasDimensions ? (["dimensions"] as DataTypeKey[]) : []),
    // Curb weight is the strongest-ranking template on the site and was the
    // only NHTSA-backed page the hub never linked to.
    ...(hasCurbWeight ? (["curb-weight"] as DataTypeKey[]) : []),
    ...(hasSafety ? (["safety-ratings"] as DataTypeKey[]) : []),
    ...(hasEv ? (["ev-range"] as DataTypeKey[]) : []),
  ];

  const snapshot = isSample
    ? buildModelSpecsSnapshot({
        makeName: make.name,
        modelName: model.name,
        vehicles,
        specs: snapshotSpecs,
        dimensions: snapshotDimensions,
        safetyRating: snapshotSafety,
      })
    : null;




  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/$make" params={{ make: make.slug }} className="hover:text-foreground">{make.name}</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{model.name}</span>
      </nav>

      {/* Header */}
      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
          {/* Keyword-complete H1: model hubs rank for "<model> mpg",
              "<model> gas mileage", "<model> engine size" and "dimensions
              <model>", so all four phrasings live in the heading pair. */}
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            {isSample && snapshot?.latestYear ? `${snapshot.latestYear} ` : ""}
            {fullName} Specs: MPG, Gas Mileage, Engine Size &amp; Dimensions
          </h1>

          {/* On rebuilt sample hubs the snapshot lead is the single intro —
              the legacy BLUF paragraph duplicated it. */}
          {!snapshot && (
            <BlufSummary
              text={buildBlufSummary({
                makeName: make.name,
                modelName: model.name,
                vehicles,
                specs: towingSpecs,
              })}
            />
          )}

          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            {yearMin && yearMax && (
              <span className="text-muted-foreground">
                {yearMin === yearMax ? `${yearMin}` : `${yearMin}–${yearMax}`} model years
              </span>
            )}
            <span className="text-muted-foreground">{totalVariants ?? vehicles.length} variants</span>
            {bestMpg && (
              <span className="text-muted-foreground">Up to {bestMpg} MPG combined</span>
            )}
          </div>
        </div>
      </section>

      {/* Scannable utility link bar */}
      <section className="border-b border-border bg-card/60">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {vehicles.length > 0 && (
            <Link
              to="/$make/$model/$dataType"
              params={{ make: make.slug, model: model.slug, dataType: "mpg" }}
              className="text-primary hover:underline"
            >
              Real-World MPG Questions
            </Link>
          )}

          {availableSpecTypes.includes("towing-capacity") && (
            <Link
              to="/$make/$model/$dataType"
              params={{ make: make.slug, model: model.slug, dataType: "towing-capacity" }}
              className="text-primary hover:underline"
            >
              Towing Limits
            </Link>
          )}
          {(availableSpecTypes.includes("bed-dimensions") || hasDimensions) && (
            <Link
              to="/$make/$model/$dataType"
              params={{
                make: make.slug,
                model: model.slug,
                dataType: availableSpecTypes.includes("bed-dimensions")
                  ? "bed-dimensions"
                  : "dimensions",
              }}
              className="text-primary hover:underline"
            >
              Bed Dimensions &amp; Cargo Space
            </Link>
          )}
          <Link
            to="/$make/$model/$dataType"
            params={{ make: make.slug, model: model.slug, dataType: "maintenance-schedule" }}
            className="text-primary hover:underline"
          >
            Service Intervals
          </Link>
        </div>
        {maintenanceYears.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">
              {fullName} maintenance schedule by model year
            </h3>
            <p className="text-sm text-muted-foreground">
              Verified year-by-year intervals for{" "}
              {maintenanceYears[maintenanceYears.length - 1]}–{maintenanceYears[0]} are listed on the{" "}
              <Link
                to="/$make/$model/$dataType"
                params={{ make: make.slug, model: model.slug, dataType: "maintenance-schedule" }}
                className="text-primary hover:underline"
              >
                {fullName} maintenance schedule
              </Link>
              , where you can switch model year in the table.
            </p>
          </div>
        )}
      </section>


      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <AdSlot placement="model" />
      </div>



      {snapshot && (
        <ModelSpecsSnapshotBlock
          makeSlug={make.slug}
          modelSlug={model.slug}
          fullName={fullName}
          snapshot={snapshot}
        />
      )}



      {/* Data type links */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <h2 className="text-lg font-semibold text-foreground mb-4">Detailed Specifications</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            // EPA-derived pages 404 for models with no EPA rows (HD trucks).
            ...ALL_DATA_TYPES.filter((dt) => !noEpa || dt === "maintenance-schedule"),

            ...SPEC_DATA_TYPES.filter((dt) => availableSpecTypes.includes(dt)),
            ...extraTypes,
          ].map((dt) => {
            const info = getDataTypeInfo(dt);
            return (
              <Link
                key={dt}
                to="/$make/$model/$dataType"
                params={{ make: make.slug, model: model.slug, dataType: info.slug }}
                className="group rounded-lg border border-border bg-card p-5 hover:border-primary hover:shadow-sm transition-all"
              >
                <h3 className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
                  {info.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {info.shortLabel} for every year and trim
                </p>
                <span className="text-xs text-primary mt-3 inline-block group-hover:underline">
                  View details →
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Overview table */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        {generations.length > 1 && (
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-foreground mb-1">
              {fullName} Generations
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Grouped by year ranges where the engine lineup, drivetrain options, and EPA
              class stay consistent — a derived approximation of the factory generations,
              not a manufacturer-published list.
            </p>
            <div className="rounded-lg border border-border bg-card overflow-x-auto">
              <table className="spec-table">
                <thead>
                  <tr>
                    <th>Years</th>
                    <th>Variants</th>
                    <th>Engines</th>
                    <th>Drive</th>
                    <th>Class</th>
                    <th>Best MPG</th>
                  </tr>
                </thead>
                <tbody>
                  {generations.map((g) => (
                    <tr key={g.label}>
                      <td data-numeric className="font-semibold text-primary">{g.label}</td>
                      <td data-numeric>{g.vehicleCount}</td>
                      <td>{g.engineSummary}</td>
                      <td>{g.driveSummary}</td>
                      <td>{g.classSummary}</td>
                      <td data-numeric>{g.bestMpg ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {noEpa ? (
          <>
            <h2 className="text-lg font-semibold text-foreground mb-2">
              {fullName} — Published Specifications
            </h2>
            {specNarrative.map((p, i) => (
              <p key={i} className="mb-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
            {specGroups.map((g) => (
              <div key={g.specType} className="mt-6">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  {fullName} {g.title}
                </h3>
                <div className="rounded-lg border border-border bg-card overflow-x-auto">
                  <table className="spec-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        {g.columns.map((c) => (
                          <th key={String(c.key)}>
                            {c.label}
                            {c.suffix ? ` (${c.suffix})` : ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r) => (
                        <tr key={r.id}>
                          <td data-numeric className="font-semibold text-primary">
                            {r.year}
                          </td>
                          {g.columns.map((c) => {
                            const v = r[c.key] as string | number | null | undefined;
                            const numeric = typeof v === "number";
                            return (
                              <td key={String(c.key)} {...(numeric ? { "data-numeric": "" } : {})}>
                                {v == null || v === ""
                                  ? "—"
                                  : numeric
                                    ? v.toLocaleString("en-US")
                                    : v}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-foreground mb-4">
              {fullName} — All Years Overview
            </h2>
            <div className="rounded-lg border border-border bg-card overflow-x-auto">
              <table className="spec-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Trim</th>
                    <th>Engine</th>
                    <th>Trans</th>
                    <th>Drive</th>
                    <th>City</th>
                    <th>Hwy</th>
                    <th>Combined</th>
                    <th>Fuel</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v: VehicleRow) => (
                    <tr key={v.id}>
                      <td data-numeric>{v.year}</td>
                      <td>{v.trim || "—"}</td>
                      <td>
                        {v.cylinders || "—"}
                        {v.displacement ? ` · ${v.displacement}L` : ""}
                      </td>
                      <td>{v.transmission || "—"}</td>
                      <td>{v.drive || "—"}</td>
                      <td data-numeric>{v.mpg_city ?? "—"}</td>
                      <td data-numeric>{v.mpg_hwy ?? "—"}</td>
                      <td data-numeric className="font-semibold text-primary">
                        {v.mpg_combined ?? "—"}
                      </td>
                      <td>{v.fuel_type_detail || v.fuel_type || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {variantsTruncated && (
              <p className="mt-3 text-sm text-muted-foreground">
                Showing the {vehicles.length} most recent EPA configurations of {totalVariants} on
                record.{" "}
                <Link
                  to="/$make/$model/$dataType"
                  params={{ make: make.slug, model: model.slug, dataType: "mpg" }}
                  className="text-primary hover:underline"
                >
                  See the full {fullName} MPG history
                </Link>
                .
              </p>
            )}

          </>
        )}
      </section>


      {comparisons.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
          <h2 className="text-lg font-semibold text-foreground mb-3">
            Compare the {fullName} with rivals
          </h2>
          <ul className="flex flex-wrap gap-2">
            {comparisons.map((c, i) => (
              <li key={c.slug}>
                <Link
                  to="/compare/$pair"
                  params={{ pair: c.slug }}
                  className={i === 0 ? "cta-gold" : "chip-link"}
                >
                  {fullName} vs {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {maintenanceSpecModelUrl(make.slug, model.slug) && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
          <MaintenanceSpecLink
            heading="Maintenance schedule"
            body={`Looking for the manufacturer-based service schedule for the ${fullName}?`}
            anchor={`View the ${model.name} maintenance schedule on MaintenanceSpec`}
            href={maintenanceSpecModelUrl(make.slug, model.slug)!}
          />
        </section>
      )}

      <RelatedLinks
        makeName={make.name}
        makeSlug={make.slug}
        modelName={model.name}
        modelSlug={model.slug}
        related={related}
        hasEpaData={vehicles.length > 0}
      />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {fullName} — Frequently Asked Questions
        </h2>
        <FaqBlock
          items={buildModelFaqs({
            makeName: make.name,
            modelName: model.name,
            vehicles,
            towingSpecs,
          })}
        />
      </section>

      <UsefulTools
        makeSlug={make.slug}
        latestYear={years.length ? years[0]! : null}
        rowCount={vehicles.length}
      />
    </div>
  );
}
